/* 
  Our theme will be an expansion of the Material UI Theme
  configurable options provided in admin/by sara
  hardcoded defaults set in MUI Theme
*/

import { css } from "styled-components";
import { rem } from "polished";

import { createTheme } from "@material-ui/core/styles";

// These will come from admin - search_page.rb, theme function
interface ColorsFromAdmin {
  primary: string;
  hover: string; // to be removed later
  button_bg: string;
  button_bg_hover: string;
  icon: string;
  focus_and_active_border: string;
  link: string;
  link_hover: string;
  price_tag_text: string;
  price_tag_bg: string;
  price_tag_bg_hover: string;
  price_tag_hover_text: string;
  calendar_date_selected_bg: string;
  calendar_instructions_bg: string;
  star: string;
  divider: string;
  active_tab?: string;
  tab_hover?: string;
  cluster_bg?: string;
  cluster_bg_hover?: string;
  cash_back_teaser_text?: string;
  warning_header_and_icon?: string;
  success_header_and_icon?: string;
  amenity_icon: string;
  icon_within_button: string;
  cluster_bg_color: string;
  cluster_text_color: string;
  cluster_hover_bg_color: string;
  cluster_hover_text_color: string;
}

// These are the same for every search page
interface UniversalColors {
  error: string;
  black: string;
  charcoal: string;
  dove: string;
  dusty: string;
  silver: string;
  cloud: string;
  white: string;
  header: string;
  body: string;
  border: string;
  form_input_text: string;
  form_input_placeholder: string;
  label_text: string;
  button_text: string;
}

interface FontFamilyFromAdmin {
  primary: string;
}

export interface ThemeEditableConfigTypes {
  colors: ColorsFromAdmin & UniversalColors;
  fonts: FontFamilyFromAdmin;
}

interface StyleObject {
  getStyles: Object;
}

interface ElementOptions {
  link: StyleObject;
  input_text: StyleObject;
  label: StyleObject;
}

interface EffectOptions {
  mildBoxShadow: StyleObject;
  mediumBoxShadow: StyleObject;
  strongBoxShadow: StyleObject;
}

interface CustomThemeOptions {
  colors: ColorsFromAdmin & UniversalColors;
  fontFamily: keyof FontFamilyFromAdmin;
  elements: ElementOptions;
  effects: EffectOptions;
}

declare module "@material-ui/core/styles/createTheme" {
  // Add objects here that are not part of the Material UI Theme Options
  interface AugmentedTheme {
    customThemeOptions: CustomThemeOptions;
  }
  // allow configuration using `createMuiTheme`
  interface ThemeOptions {
    customThemeOptions?: CustomThemeOptions;
  }
}

const theme = (configs: ThemeEditableConfigTypes) => {
  // These will all eventually be from Admin
  const defaultAdminColors = {
    active_tab: "#2E3143",
    tab_hover: "#C7AF88",
    link: "#595E7E",
    link_hover: "#E4CFAC",
    price_tag_bg: "#186C72",
    price_tag_bg_hover: "rgba(24, 108, 114, 0.7)",
    cluster_bg: "#1D524F",
    cluster_bg_hover: "rgba(29, 82, 79, 0.7)",
    calendar_date_selected_bg: "#2E3143",
    calendar_instructions_bg: "#C7AF88",
    cash_back_teaser_text: "#FFAA98",
    warning_header_and_icon: "#C59849",
    success_header_and_icon: "#30603F",
    star: "#785827",
    divider: "#ECE4D5",
  } as ColorsFromAdmin;

  const GlobalColors = {
    error: "#DC3915",
    black: "#000",
    charcoal: "#343638",
    dove: "#666666",
    dusty: "#999999",
    silver: "#C6C6C6",
    cloud: "#E8E8E8",
    white: "#fff",
    header: "#000",
    body: "#343638",
    border: "#E8E8E8",
    label_text: "#343638",
    form_input_text: "#343638",
    form_input_placeholder: "#666666",
    button_text: "#fff",
  } as UniversalColors;

  const MediumBoxShadowStyles = () => {
    return css`
      box-shadow: 0px 1px 15px 0px #00000014;
    `;
  };

  const MildBoxShadowStyles = () => {
    return css`
      box-shadow: 0px 1px 15px 0px #0000000a;
    `;
  };

  const StrongBoxShadowStyles = () => {
    return css`
      box-shadow: 0px 0px 15px 5px #00000024;
    `;
  };

  const LabelStyles = () => {
    return css`
      font-size: ${rem("12px")};
      color: ${GlobalColors.label_text};
      line-height: ${rem("16px")};
      letter-spacing: ${rem("0.06px")};
    `;
  };

  const LinkStyles = () => {
    return css`
      color: ${configs.colors.link};
      &:hover {
        color: ${configs.colors.link_hover};
      }
    `;
  };

  const InputTextStyles = () => {
    return css`
      font-size: ${rem("16px")};
      font-weight: 400;
      color: ${GlobalColors.form_input_text};
      line-height: ${rem("18px")};
      letter-spacing: normal;
      &::placeholder {
        color: ${GlobalColors.form_input_placeholder};
      }
    `;
  };

  const customThemeConfigs = {
    colors: {
      primary: configs.colors.primary,
      hover: configs.colors.hover,
      button_bg: configs.colors.button_bg,
      button_bg_hover: configs.colors.button_bg_hover,
      active_tab: configs.colors.active_tab || defaultAdminColors.active_tab,
      tab_hover: configs.colors.tab_hover || defaultAdminColors.tab_hover,
      link: configs.colors.link,
      link_hover: configs.colors.link_hover,
      focus_and_active_border: configs.colors.focus_and_active_border,
      price_tag_text: configs.colors.price_tag_text,
      price_tag_bg: configs.colors.price_tag_bg,
      price_tag_bg_hover: configs.colors.price_tag_bg_hover,
      price_tag_hover_text: configs.colors.price_tag_hover_text,
      cluster_bg: configs.colors.cluster_bg || defaultAdminColors.cluster_bg,
      cluster_bg_hover:
        configs.colors.cluster_bg_hover || defaultAdminColors.cluster_bg_hover,
      calendar_date_selected_bg: configs.colors.calendar_date_selected_bg,
      calendar_instructions_bg: configs.colors.calendar_instructions_bg,
      cash_back_teaser_text:
        configs.colors.cash_back_teaser_text ||
        defaultAdminColors.cash_back_teaser_text,
      warning_header_and_icon:
        configs.colors.warning_header_and_icon ||
        defaultAdminColors.warning_header_and_icon,
      success_header_and_icon:
        configs.colors.success_header_and_icon ||
        defaultAdminColors.success_header_and_icon,
      star: configs.colors.star || defaultAdminColors.star,
      icon: configs.colors.icon,
      amenity_icon: configs.colors.amenity_icon,
      icon_within_button: configs.colors.icon_within_button,
      divider: configs.colors.divider || defaultAdminColors.divider,
      cluster_bg_color: configs.colors.cluster_bg_color,
      cluster_text_color: configs.colors.cluster_text_color,
      cluster_hover_bg_color: configs.colors.cluster_hover_bg_color,
      cluster_hover_text_color: configs.colors.cluster_hover_text_color,
      ...GlobalColors,
    },
    fontFamily: configs.fonts.primary,
    effects: {
      mildBoxShadow: { getStyles: MildBoxShadowStyles() },
      mediumBoxShadow: { getStyles: MediumBoxShadowStyles() },
      strongBoxShadow: { getStyles: StrongBoxShadowStyles() },
    },
    elements: {
      link: {
        getStyles: LinkStyles(),
      },
      input_text: {
        getStyles: InputTextStyles(),
      },
      label: {
        getStyles: LabelStyles(),
      },
    },
    materialUIOverrides: {
      // Lab components do not exist in Material UI's overrides
      MuiPaper: {
        rounded: {
          borderRadius: "16px !important",
        },
      },
      MuiAutocomplete: {
        popper: {
          zIndex: "100000"
        },
        root: {
          height: "100%",
        },
        paper: {
          borderRadius: "16px !important",
        },
        input: {
          cursor: "pointer",
          color: `${GlobalColors.form_input_text}`,
          "&::placeholder": {
            color: `${GlobalColors.form_input_placeholder}`,
            opacity: 1,
          },
        },
        inputRoot: {
          height: "100%",
          borderRadius: 0,
          color: `${GlobalColors.form_input_text}`,
          '&[class*="MuiFilledInput-root"]': {
            color: `${GlobalColors.form_input_text}`,
            paddingTop: 20,
            paddingLeft: 11,
            paddingBottom: 2,
            background: "#fff",
            borderTop: `1px solid ${GlobalColors.cloud}`,
            borderLeft: `1px solid ${GlobalColors.cloud}`,
            borderBottom: `1px solid ${GlobalColors.cloud}`,
            transition: "none",
            '&[class*="Mui-focused"]': {
              border: `1px solid ${configs.colors.focus_and_active_border}`,
            },
          },
        },
        hasPopupIcon: {
          backgroundColor: "transparent",
        },
        option: {
          borderBottom: `1px solid ${GlobalColors.cloud}`,
          paddingTop: 10,
          paddingBottom: 10,
          paddingRight: 32,
          "&:first-child": {
            borderTop: "none",
          },
          "&:last-child": {
            borderBottom: "none",
          },
          '&[data-focus="true"]': {
            backgroundColor: `${GlobalColors.cloud} !important`,
          },
          '&[aria-selected="true"]': {
            backgroundColor: "#fff",
          },
        },
        listbox: {
          background: "transparent",
          border: `1px solid ${GlobalColors.cloud}`,
          padding: "0",
          borderRadius: "4px",
        },
      },
      MuiSelect: {
        root: {
          backgroundColor: `${GlobalColors.white}`,
          height: "100%",
          color: `${GlobalColors.form_input_text}`,
          transition: "none",
        },
        selectMenu: {
          backgroundColor: `${GlobalColors.white}!important`,
          padding: "25px 0px 0px 15px;",
          position: "relative",
          height: "30px",
        },
        icon: {
          transform: "translate(-10px)",
        },
        nativeInput: {
          borderColor: `${GlobalColors.white} !important`,
        },
      },
    },
  } as CustomThemeOptions;

  const expandedMaterialTheme = createTheme({
    palette: {
      primary: {
        main: customThemeConfigs.colors.button_bg,
      },
      secondary: {
        main: customThemeConfigs.colors.button_bg_hover,
      },
      contrastThreshold: 3,
      tonalOffset: 0.2,
    },
    typography: {
      fontFamily: customThemeConfigs.fontFamily,
    },
    // Extension of Material UI Theme
    customThemeOptions: {
      ...customThemeConfigs,
    },
  });

  return expandedMaterialTheme;
};

export default theme;
